import store2 from "store2";
import axios, { AxiosInstance } from "axios";
import { refreshToken } from "./useAuth";
import qs from "qs";
import { endSession, hasSessionExpired, isSessionClearedEvent, sendTabToLogin } from "./session";

const env = process.env.NODE_ENV;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const localhost = "http://localhost:8055";

export const baseUrl = env === "development" ? localhost : "https://backend.neuroimprove.pt";

export const servicesUrl = env === "development" ? "https://services.neuroimprove.pt" : "https://services.neuroimprove.pt";

export const financeBaseUrl = env === "development" ? "https://finance.neuroimprove.pt" : "https://finance.neuroimprove.pt";

const REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

// Routes that should be routed to the finance instance
export const FINANCE_ROUTES = [
	"/banks",
	"/bank_transactions",
	"/transaction_categories",
	"/parent_transaction_categories",
	"/vendors",
	"/company",
	"/staff_remuneration",
	"/staff_monthly_payments",
	"/operational_centers"
];

// Users that should login to finance instance
export const FINANCE_USERS = [
	"jorge.perestrelo@neuroimprove.pt",
	"shilan.aslani@neuroimprove.pt",
	"admin@neuroimprove.pt",
	"ipereira@valora.pt",
	"claude@neuroimprove.pt"
];

const refreshPromises: Record<string, Promise<any> | null> = {
	auth: null,
	finance_auth: null
};

const createShouldRefreshToken = () => {
	return (tokens: any) => {
		if (!tokens?.access_token || !tokens?.expires_at) {
			return false;
		}

		const currentTime = Date.now();
		const expirationTime = tokens.expires_at;

		return expirationTime - currentTime <= REFRESH_THRESHOLD_MS;
	};
};

const createGetFreshTokens = (storageKey: string) => {
	const shouldRefreshToken = createShouldRefreshToken();

	return async (performTokenRefresh: (refresh_token: string) => Promise<any>) => {
		const tokens = store2.get(storageKey);

		if (!tokens) {
			window.location.replace("/login");
			return null;
		}

		if (!tokens.refresh_token) {
			if (hasSessionExpired(tokens, Date.now())) {
				endSession("/login");
				throw new Error("Session ended");
			}
			return tokens;
		}

		if (refreshPromises[storageKey]) {
			try {
				const newTokens = await refreshPromises[storageKey];
				return newTokens;
			} catch (error) {
				refreshPromises[storageKey] = null;
				throw error;
			}
		}

		if (shouldRefreshToken(tokens)) {
			return await performTokenRefresh(tokens.refresh_token);
		}

		return tokens;
	};
};

const createPerformTokenRefresh = (storageKey: string, apiBaseUrl: string) => {
	return async (refresh_token: string) => {
		if (refreshPromises[storageKey]) {
			return await refreshPromises[storageKey];
		}

		refreshPromises[storageKey] = refreshToken(refresh_token, apiBaseUrl)
			.then((updatedTokens) => {
				const expiresAt = Date.now() + updatedTokens.expires * 1000;
				const tokensWithExpiration = {
					...updatedTokens,
					expires_at: expiresAt
				};

				store2.set(storageKey, tokensWithExpiration);
				refreshPromises[storageKey] = null;
				return tokensWithExpiration;
			})
			.catch((error) => {
				refreshPromises[storageKey] = null;
				store2.remove(storageKey);
				window.location.replace("/login");
				throw error;
			});

		return await refreshPromises[storageKey];
	};
};

const createApiInstance = (apiBaseUrl: string, storageKey: string): AxiosInstance => {
	const instance = axios.create({
		paramsSerializer: {
			serialize: (params) => {
				return qs.stringify(params, { encode: false });
			}
		},
		baseURL: apiBaseUrl
	});

	const performTokenRefresh = createPerformTokenRefresh(storageKey, apiBaseUrl);
	const getFreshTokens = createGetFreshTokens(storageKey);

	instance.interceptors.request.use(
		async (config) => {
			try {
				const tokens = await getFreshTokens(performTokenRefresh);

				if (!tokens) {
					return config;
				}

				config.headers.set("Authorization", `Bearer ${tokens.access_token}`);
				return config;
			} catch (error) {
				window.location.replace("/login");
				return Promise.reject(error);
			}
		},
		(error) => {
			return Promise.reject(error);
		}
	);

	instance.interceptors.response.use(
		(res) => {
			return res;
		},
		async (err) => {
			const originalConfig = err?.config;

			if (err.response?.status === 401 && !originalConfig._retry) {
				originalConfig._retry = true;

				try {
					const tokens = store2.get(storageKey);
					if (!tokens?.refresh_token) {
						endSession("/login");
						return Promise.reject(err);
					}

					const newTokens = await performTokenRefresh(tokens.refresh_token);
					originalConfig.headers["Authorization"] = `Bearer ${newTokens.access_token}`;

					return instance(originalConfig);
				} catch (refreshError) {
					window.location.replace("/login");
					return Promise.reject(err);
				}
			}

			return Promise.reject(err);
		}
	);

	return instance;
};

const mainApi = createApiInstance(baseUrl, "auth");
const financeApi = createApiInstance(financeBaseUrl, "finance_auth");

const getFreshOperationsTokens = createGetFreshTokens("auth");
const performOperationsTokenRefresh = createPerformTokenRefresh("auth", baseUrl);

export const getOperationsAccessToken = async () => {
	const operationsTokens = await getFreshOperationsTokens(performOperationsTokenRefresh);
	const operationsAccessToken: string | null = operationsTokens?.access_token ?? null;
	return operationsAccessToken;
};

export const renewOperationsAccessToken = async (refusedAccessToken: string) => {
	const storedTokens = store2.get("auth");
	if (storedTokens?.access_token && storedTokens.access_token !== refusedAccessToken) {
		const alreadyRenewedAccessToken: string = storedTokens.access_token;
		return alreadyRenewedAccessToken;
	}
	if (!storedTokens?.refresh_token) {
		endSession("/login");
		throw new Error("Session ended");
	}
	const renewedTokens = await performOperationsTokenRefresh(storedTokens.refresh_token);
	const renewedAccessToken: string = renewedTokens.access_token;
	return renewedAccessToken;
};

const api = new Proxy(mainApi, {
	apply(target, thisArg, argumentsList) {
		const config = argumentsList[0];
		const url = config?.url || "";

		if (shouldUseFinanceInstance(url)) {
			const accessError = checkFinanceAccess();
			if (accessError) return accessError;
			return financeApi(config);
		}

		return Reflect.apply(target, thisArg, argumentsList);
	},
	get(target, prop) {
		if (prop === "request" || prop === "get" || prop === "post" || prop === "put" || prop === "patch" || prop === "delete") {
			return (...args: any[]) => {
				const url = args[0] || "";

				if (shouldUseFinanceInstance(url)) {
					const accessError = checkFinanceAccess();
					if (accessError) return accessError;
					return (financeApi as any)[prop](...args);
				}

				return (target as any)[prop](...args);
			};
		}

		return (target as any)[prop];
	}
});

window.addEventListener("storage", (e) => {
	if (isSessionClearedEvent(e)) {
		sendTabToLogin();
	}
});

const shouldUseFinanceInstance = (url: string) => {
	return FINANCE_ROUTES.some((route) => url.includes(route));
};

export const isFinanceEntity = (entity: string) => {
	return FINANCE_ROUTES.some((route) => route.includes(entity));
};

const checkFinanceAccess = () => {
	const financeAuth = store2.get("finance_auth");
	if (!financeAuth) {
		return Promise.reject({
			response: {
				status: 403,
				data: {
					errors: [
						{
							message: "Sem permissões",
							extensions: {
								code: "FORBIDDEN"
							}
						}
					]
				}
			},
			message: "Sem permissões"
		});
	}
	return null;
};

export const getApiInstance = (instanceToUse?: string) => {
	switch (instanceToUse) {
		case "finance":
			return financeApi;
		default:
			return api;
	}
};

export const getApiInstanceForEntity = (entity: string) => {
	const isFinance = isFinanceEntity(entity);
	const financeAuth = store2.get("finance_auth");

	if (isFinance && !financeAuth) {
		throw {
			response: {
				status: 403,
				data: {
					errors: [
						{
							message: "Sem permissões",
							extensions: {
								code: "FORBIDDEN"
							}
						}
					]
				}
			},
			message: "Sem permissões"
		};
	}

	const instanceToUse = isFinance ? "finance" : undefined;
	return getApiInstance(instanceToUse);
};

export { api, financeApi };

type InstanceConfig = {
	url: string;
	authKey: string;
};

export const getInstanceConfig = (instanceToUse?: string): InstanceConfig => {
	switch (instanceToUse) {
		case "finance":
			return {
				url: financeBaseUrl,
				authKey: "finance_auth"
			};
		default:
			return {
				url: baseUrl,
				authKey: "auth"
			};
	}
};
